import { VideoPlayerDetails } from "../../types/VideoPlayerDetails.ts";
import React, { useEffect, useState } from "react";
import store from "../../cpac-store.ts";
import { truncateWords } from "../../utils/search-utils.ts";
import { FaPlay } from "../../icons/faPlay";
import { FaArrowLeft } from "../../icons/faArrowLeft";
import { FaArrowRight } from "../../icons/faArrowRight";
import SharePageReact from "../../share/SharePageReact.tsx";
import { CpacJwPlayerContainer } from "./CpacJwPlayerContainer";
import { JwPlaylistItem } from "../../types/JwPlaylistItem.ts";
import { Tag } from "../../types/Tag.ts";
import { loadAppropriateImages } from "../../utils/image-utils.ts";
import { DateTime, Duration } from "luxon";
import RelativeTime from "../../date/RelativeTime.tsx";
import LiveCountdown from "../../date/LiveCountdown.tsx";
import {getLabel} from "../../language/lang-utils.ts";
import GetLabel from "../../language/GetLabel.tsx";
import { fetchEpisodeInfo } from "../../utils/backend.ts";
import { config } from "../../config.ts";
import { AudioLang, ManifestAudioTrack, normalizeAudioLang } from "./audio-tracks.ts";
import { getLanguage } from "../../language/lang-utils.ts";

// Reveal the player as soon as it reports ready. Tunable — raise this to hold the loading shimmer
// up for a minimum time (ms) so it doesn't just flash by on fast loads.
const SHIMMER_MIN_VISIBLE_MS = 0;

// Programs that go live closer to their scheduled start (shorter prelive lead time).
const EARLY_LIVE_PROGRAM_IDS: string[] = ["7", "23", "51", "63"];

type VideoPropTypes = {
  id: string;
  details: VideoPlayerDetails;
  isLanding: boolean;
  processPrelive: boolean;
  hOnlyItem: boolean;
  setShowCPACTV?: React.Dispatch<React.SetStateAction<boolean>>;
  onPlaybackState?: (isPlaying: boolean) => void;
  tags?: Tag[];
};

const Video = (props: VideoPropTypes) => {
  // The language the audio SHOULD be on: page language initially, then whatever button the
  // user clicks. The player container reconciles the actual track toward this value.
  const [videoLang, setVideoLang] = useState<AudioLang | undefined>();
  const [playlist, setPlaylist] = useState<JwPlaylistItem>();
  const [isLiveNow, setIsLiveNow] = useState(false);
  const [newUrl, setNewUrl] = useState(null);
  // Audio renditions from the stream's master manifest — one button each.
  const [audioTracks, setAudioTracks] = useState<ManifestAudioTrack[]>([]);
  // The track the player has CONFIRMED it is on — drives the buttons' selected state.
  const [activeLang, setActiveLang] = useState<AudioLang | undefined>();
  // Bumped on every language-button click; the container starts playback on each bump.
  const [playSignal, setPlaySignal] = useState(0);
  const [isWide, setIsWide] = useState(false);
  const [audioMessage, setAudioMessage] = useState<string | undefined>();
  // The player area reserves its 16:9 height from first paint (see .jw-area) so the content below
  // doesn't jump while the JW player loads. A shimmer fills that reserved box until the player is
  // ready, then fades out.
  const [playerReady, setPlayerReady] = useState(false);

  const lang = getLanguage();

  // Safety net: if the player never fires onReady (e.g. a video that fails to load), lift the
  // shimmer anyway after a few seconds so it can't shimmer forever.
  useEffect(() => {
    if (playerReady) return;
    const t = setTimeout(() => setPlayerReady(true), 10000);
    return () => clearTimeout(t);
  }, [playerReady]);

  // Where the player is mounted — derived, not state: the video page always mounts with a
  // query string (?id=...), and landing pages say so via props. Fixed for the page's life.
  const isInVideoPage = window.location.search !== "";
  const isInLandingPage = props.isLanding;

  // The uploadDate metadata: the live start wins when it is newer than the last-modified
  // stamp; the 24/7 cpactv stream has no meaningful upload date.
  const lastDateModified: DateTime | undefined =
    props.details.type !== "cpactv"
      ? props.details.liveDateTime &&
        props.details.lastDateModified &&
        props.details.liveDateTime > props.details.lastDateModified
        ? props.details.liveDateTime
        : props.details.lastDateModified
      : undefined;

  const startDurationArr =
    props && props.details && props.details.videoDuration
      ? props.details.videoDuration.split(":")
      : undefined;
  let isoDuration = null;
  if (startDurationArr) {
    const hrs = parseInt(startDurationArr[0]);
    const mins = parseInt(startDurationArr[1]);
    const secs = parseInt(startDurationArr[2]);
    isoDuration = Duration.fromObject({
      hours: hrs,
      minutes: mins,
      seconds: secs,
    }).toISO();
  }

  useEffect(() => {
    const tmpPlayList: JwPlaylistItem = {
      title: props.details.title,
      description: props.details.description,
      category: props.details.category,
      image: props.details.image,
      file: newUrl !== null ? newUrl : props.details.videoUrl,
    };
    if (props.details.videoUrl) {
      setPlaylist(tmpPlayList);
      // Requirement: initial audio language = the page's language. Functional update so the
      // effect doesn't depend on videoLang — it only ever fills the initial undefined.
      setVideoLang((prev) => prev ?? normalizeAudioLang(store.getState().lang.toString()));
    }
  }, [props.details, newUrl]);

  // Hydrate lazy data-src images (the prelive thumbnail) after the renders that can add one
  // — previously ran unconditionally on every render.
  useEffect(() => {
    loadAppropriateImages();
  }, [isLiveNow, newUrl, props.details.image]);

  const audioLangClickHandler = (lang: AudioLang) => {
    setVideoLang(lang);
    // Same-language re-clicks must still (re)start playback; the reconciler is idempotent.
    setPlaySignal((n) => n + 1);
  };

  const renderTags = () => {
    if (props.tags) {
      return props.tags.map((tag: Tag, index: number) => {
        return (
          <a href={tag.url} key={index} aria-label={getLabel("VIDEO_TAGGED") + " " + tag.name}>
            <div className="article-tag">
              <img alt="tag icon" className="tag-icon" src="/static-assets/theme/svg/tag.svg"/>
              <span itemProp="keywords">{tag.name}</span>
            </div>
          </a>
        );
      });
    }
  };

  const audioBtnLabel = (lang: AudioLang): string =>
    lang === "en" ? "ENGLISH" : lang === "fr" ? "FRENCH" : "FLOOR";

  const renderAudioBtns = () => {
    return audioTracks.map((track) => {
      return (
        <button key={`audio-btn-${track.lang}`} aria-pressed={activeLang === track.lang} onClick={() => audioLangClickHandler(track.lang)}>
          <FaPlay />
          <GetLabel name={audioBtnLabel(track.lang)} />
        </button>
      );
    });
  };

  const renderWide = () => {
    if (!isWide) {
      return (
        <>
          <span>
            <GetLabel name={"WIDE_PLAYER"} />
          </span>
          <FaArrowRight />
        </>
      );
    } else {
      return (
        <>
          <span>
            <GetLabel name={"CONSTRAIN_PLAYER"} />
          </span>
          <FaArrowLeft />
        </>
      );
    }
  };

  const toggleWide = () => {
    setIsWide((isWide) => !isWide); // needed to track & trigger rerender
    const el = document.getElementById(props.id); // this div is outside the component, so need to rerender ourselves
    if (el != null) {
      if (el.classList.contains("wide")) {
        el.classList.remove("wide");
      } else {
        el.classList.add("wide");
      }
    }
  };
  //description render
  const renderDescription = () => {
    const regX = /\n/g;
    const fixedDescription = props.details.description
      ? props.details.description.replace(regX, "<br>")
      : "";
    return isInVideoPage ? (
      <p className="description" dangerouslySetInnerHTML={{ __html: fixedDescription }}></p>
      ) : isInLandingPage ? (
      <p className="description-landing" dangerouslySetInnerHTML={{ __html: fixedDescription }}></p>
      ) : (
      <p className="description description__container-relative" itemProp="description">
        {props.details.description &&
          truncateWords(props.details.description, 225)}
        {props.details.description &&
          props.details.description.length > 225 && (
            <span className="description__ellipsis" onClick={() => (window.location.href = props.details.url)}>
              (...)
            </span>
          )}
      </p>
    );
  };

  const minutesBefore = config.liveNotificationTime;
  // liveDateTime is undefined for a corrupt/incomplete item (parseDate returns undefined
  // for blank or off-contract dates, and the type now says so). Guard the date math so a
  // missing date degrades gracefully instead of throwing during render — an uncaught
  // throw here blanks the entire featured list via ItemListErrorBoundary.
  const liveDateTime = props.details.liveDateTime;
  const eventIsSoon = liveDateTime != null
      && DateTime.now() > liveDateTime.minus(minutesBefore)
      && DateTime.now() < liveDateTime;
  const programID = props.details.categoryURL.replace("/program?id=", "");

  // Whether this render shows the prelive countdown — the same condition renderJWPlayer uses
  // to pick the thumbnail branch over the player branch.
  const isCountingDown =
    props.processPrelive &&
    props.details.type === "prelive" &&
    !isLiveNow &&
    newUrl === null;

  // Poll for the live transition while the countdown is showing. Lives in an effect WITH
  // cleanup — the old code created a fresh, never-cleared interval inside the render
  // function on every re-render, so several could tick at once and each could fire its own
  // fetchEpisodeInfo at the transition. When the start threshold passes: clear immediately
  // (so this interval can never fire twice), flip to live, and fetch the live stream URL;
  // flipping isLiveNow also ends the countdown, which unwinds the effect via its cleanup.
  useEffect(() => {
    if (!isCountingDown || liveDateTime == null) return;
    const startTime = liveDateTime;
    // Certain programs (QP/proceedings) go live closer to their scheduled start.
    const lead = EARLY_LIVE_PROGRAM_IDS.includes(programID) ? config.liveInTwo : config.liveInFive;
    const liveTimer = setInterval(() => {
      if (DateTime.now() > startTime.minus(lead)) {
        clearInterval(liveTimer);
        setIsLiveNow(true);
        fetchEpisodeInfo(props.details.episodeId).then((data) => {
          setNewUrl(data.component.details.videoUrl);
        });
      }
    }, 1000);
    return () => clearInterval(liveTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCountingDown, liveDateTime?.toMillis(), programID, props.details.episodeId]);

  // Pure render: the countdown UI only (the live-transition polling is the effect above).
  const renderCounter = () => {
    if (liveDateTime == null) return; // no valid start date — nothing to count down to
    return (
      <div className="live-info">
        <GetLabel name={"LIVE"} />{" "}
        {eventIsSoon ? (
          <RelativeTime date={liveDateTime} />
        ) : (
          <LiveCountdown liveDateTime={liveDateTime} />
        )}
      </div>
    );
  };

  const renderTest = () => {
		return (
			<div className="video-test-label t-r">
				<span>TEST</span>
			</div>
		)
	}

  const handleAudioMessage = (message: string | undefined) => {
    setAudioMessage(message);
  };

  const renderAudioMessage = () => {
    if (audioTracks.length > 0 && audioMessage) {
      return <p className='audio_message'>{audioMessage}</p>
    }
  }

  const renderJWPlayer = () => {
    if (
      props.processPrelive &&
      props.details.type === "prelive" &&
      !isLiveNow &&
      newUrl === null
    ) {
      return (
        <div className="video-thumb-container">
          <div className="video-thumb-header">
            <img className="video-thumb multi-thumb" loading="lazy" data-src={props.details.image} alt={props.details.altImageTxt} itemProp="thumbnailUrl"/>
            {renderCounter()}
          </div>
          {props.details.teststream === "true" ? renderTest() : ""}
        </div>
      );
    } else {
      // Always render the player area so its 16:9 height is reserved immediately — otherwise the
      // content below (description / tags) jumps to the top until the JW player loads and pushes it
      // down. The shimmer fills the reserved box while the player boots, then fades out on ready.
      return (
        <div itemProp="video" className="jw-area">
          <meta itemProp="thumbnailUrl" content={props.details.image} />
          <div className={`video-skeleton${playerReady ? " video-skeleton--hidden" : ""}`} aria-hidden="true"/>
          {playlist && playlist.file && videoLang && (
            <CpacJwPlayerContainer
              desiredLang={videoLang}
              playSignal={playSignal}
              playlist={playlist}
              setShowCPACTV={
                isInVideoPage && isInLandingPage ? null : props.setShowCPACTV ?? null
              }
              autoplay={false}
              onTracks={setAudioTracks}
              onActiveLang={setActiveLang}
              onAdoptLang={setVideoLang}
              vType={props.details.type}
              onMessage={handleAudioMessage}
              onPlaybackState={props.onPlaybackState}
              onPlayerReady={() =>
                setTimeout(() => setPlayerReady(true), SHIMMER_MIN_VISIBLE_MS)
              }
            />
          )}
          {props.details.teststream === "true" ? renderTest() : ""}
        </div>
      );
    }
  };

  const videoSchemaType = () => {
    return props.details.type !== "cpactv"
      ? "https://schema.org/VideoObject"
      : "https://schema.org/Episode";
  };
  const lastModDateStr =
    lastDateModified === undefined || lastDateModified === null
      ? null
      : lastDateModified.toISO();
  
  const itemLiveTimeEN = props.details.liveDateTime && props.details.type == 'video' ? props.details.liveDateTime.toUTC().setLocale('en') : props.details.liveDateTime && (props.details.type == 'live' || props.details.type == 'prelive') ? props.details.liveDateTime.setLocale('en') : "";
  const itemLiveTimeFR = props.details.liveDateTime && props.details.type == 'video' ? props.details.liveDateTime.toUTC().setLocale('fr') : props.details.liveDateTime && (props.details.type == 'live' || props.details.type == 'prelive') ? props.details.liveDateTime.setLocale('fr') : "";

  const liveDateTimeEn = String(itemLiveTimeEN?.toLocaleString(DateTime.DATE_FULL));
  const liveDateTimeFr = String(itemLiveTimeFR?.toLocaleString(DateTime.DATE_FULL));
  
  return (
    <div id="video-player-holder">
      <div itemScope itemType={videoSchemaType()}>
        <meta itemProp="name" content={props.details.title} />
        <meta itemProp="description" content={props.details.description} />
        {props.processPrelive && props.details.type === "live" && (
          <meta
            itemType="https://schema.org/BroadcastEvent"
            itemProp="isLiveBroadcast"
          />
        )}
        {renderJWPlayer()}
        {lastModDateStr && (
          <meta itemProp="uploadDate" content={lastModDateStr} />
        )}
        <div className="video-info">
          <div
            className={`video-info__btns-cont ${
              isInLandingPage ? "centered" : ""
            }`}
          >
            {renderAudioBtns()}
            <button
              className={isInLandingPage || props.hOnlyItem ? "hide" : ""}
              onClick={toggleWide}
            >
              {renderWide()}
            </button>
          </div>
          <div className="video-info__details-cont">
            <meta
              itemProp={`${
                props.details.type !== "cpactv" ? "contentUrl" : "url"
              }`}
              content={props.details.videoUrl}
            />
            {isoDuration && <meta itemProp="duration" content={isoDuration} />}
            <div itemScope itemType="https://schema.org/ReportageNewsArticle">
            {audioMessage && renderAudioMessage()}
              {props.details.type === "cpactv" && !isInVideoPage ?
                <a href={props.details.url} className="category">
                  <span itemProp="isPartOf">{props.details.category}</span>
                </a>
              :
              props.details.type === "cpactv" && isInVideoPage ?
              <a href={props.details.categoryURL} className="category">
                  <span itemProp="isPartOf">{props.details.category}</span>
              </a>
              :
              !isInLandingPage ?
                <a href={props.details.categoryURL} className="category">
                  <span itemProp="isPartOf">{props.details.category}</span>
                </a>
                : ""
              }
              {!isInVideoPage && !isInLandingPage ?
               <h3>
                <a href={props.details.url} className="title featured" aria-label={props.details.category + " - " + props.details.title}>
                  <span itemProp="headline">{props.details.title}</span>
                </a>
               </h3>
               :
               <>
                {props.details.title != liveDateTimeEn && props.details.title != liveDateTimeFr && props.details.type !== "cpactv" ?
                <div className="episode-date">
                  {props.details.liveDateTime && lang == "en" && props.details.title != liveDateTimeEn && !props.details.title.includes(liveDateTimeEn) ? liveDateTimeEn : props.details.liveDateTime && lang == "fr" && !props.details.title.includes("1er") && props.details.title != liveDateTimeFr && !props.details.title.includes(liveDateTimeFr) ? liveDateTimeFr : ""}
                </div>
                : ""}
                <h2 className={`title ${isInVideoPage ? "in-video-page" : isInLandingPage ? "in-landing-page" : ""}`}
                  itemProp="headline">
                  {props.details.title}
                </h2>
                </>
              }

              <meta
                itemProp="articleBody"
                content={props.details.description}
              />
              <meta itemProp="image" content={props.details.image} />
              {renderDescription()}
            </div>
          </div>
          {props.tags && props.tags.length > 0 && (
            <div className="article-tag-container">{renderTags()}</div>
          )}
          {isInVideoPage && !isInLandingPage && <SharePageReact />}
        </div>
      </div>
    </div>
  );
};

export default Video;
